import React, { useEffect, useState } from 'react';
import { isAndroid, isIOS, isMobile } from 'react-device-detect';
import { useSelector, useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import queryString from 'query-string';
import {
  BaseBox,
  Body2,
  DoconInput,
  DoconInputWrapper,
  FlexBox,
  InputPrefix,
  PrimaryButton,
} from '@docon/docon-ui-library';
import EmrSMSModal from './EmrSMSModal';
import SliderPane from './SliderPane';
import { getUserDetailsFromToken, login, Status, updateAuthState } from 'redux/reducer/auth';
import { environment } from 'environments/environment';
import { cleverTapAnl } from 'utilities';
import Image from 'shared/Image';
import { downloadApp, downloadImage } from 'shared/DownloadApp';
import { eventService, headerKey, restClient, storageService } from 'services';
import { getNextPathname } from 'utils';
import { colors } from 'utilities/colorConfig';
import { EventLoginNumberSubmit } from 'utilities/clevertapEvent';
// import { myUser } from 'app/app';
import firebaseService from 'app/firebaseSetup';
import { checkIsWebview } from 'utils/webview/webview';
import { useCookies } from 'react-cookie';
import getAuthDataFromWebview from 'utils/webview/webview';
import { updateAppState } from 'redux/reducer/app';
import { cookieNames } from 'utils/constants';

const BASE_URL = environment.REACT_APP_BASE_URL;

const Section = styled(FlexBox)`
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: space-between;
  width: 50%;
`;

const Form = styled(BaseBox)`
  position: relative;
  max-width: 325px;
  margin: auto;
  text-align: left;

  label {
    margin-bottom: 10px;
    font-size: 21px;
    line-height: 30px;
    font-weight: 700;
    color: currentColor;
  }

  a {
    text-decoration: none;
    color: ${colors.primaryBlue};
  }

  ${InputPrefix} {
    color: ${colors.subHeading};
    width: 50px;
  }

  ${DoconInputWrapper} {
    --inp-height: 48px;
  }
`;

const ErrorText = styled(Body2)`
  position: absolute;
  width: 100%;
  padding-top: 8px;
  font-weight: 500;
  color: ${colors.error};
  text-align: center;
`;

const Footer = styled(FlexBox)`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  height: 55px;
  align-items: center;
  justify-content: center;
  background-color: #f2f3f8;
  gap: 16px;
`;

const Root = styled(FlexBox)`
  height: 100vh;
  text-align: center;

  .infoText {
    margin: 12px 0 16px;
  }
  
  ${() => isMobile && css`
    height: 100%;
    flex-direction: column;
    flex-wrap: nowrap;

    .infoText {
      margin: 8px 0 10px;
    }

    ${Section} {
      width: 100%;
    }

    ${Form} {
      margin: 28px 20px 40px;
      max-width: 100%;

      label {
        font-size: 18px;
        line-height: 28px;
      }
    }
  `}
`;

export type IProps = RouteComponentProps & {
  onlyForm?: boolean;
  hideInfoText?: boolean;
  formClassName?: string;
  onLoginSuccess?: (x: any) => void;
};

const checkVisibility = () => (isIOS || isAndroid) ? true : false;

function Login({
  history,
  onlyForm,
  hideInfoText,
  formClassName,
  onLoginSuccess,
}: IProps) {
  const location = useLocation();
  const searchParams = queryString.parse(location.search);
  const [mobileNumber, setMobileNumber] = useState(searchParams.m || null);
  const [isWebviewError, setIsWebviewError] = useState(false);
  const {
    accessToken,
    isLogging,
    loginStatus,
    loginError,
  } = useSelector(state => state.auth);

  const { isWebView } = useSelector(state => state.app);
  const dispatch = useDispatch();

  useEffect(() => {
    if (accessToken)
      history.replace({
        pathname: getNextPathname(searchParams),
        search: location.search,
      });
  }, [accessToken]);

  const [cookies, setCookies] = useCookies([cookieNames.WEBVIEW_COOKIE]);

  useEffect(() => {
    if (checkIsWebview(cookies)) {
      // If 'doconParams' exists, it sets a value in the storageService indicating that this is a PE WebView environment.
      storageService.set(storageService.isPEWebView, true);
      //dispatches an action to update the application state to reflect that the environment is a WebView.
      dispatch(updateAppState({ isWebView: true }));
    }
  }, [cookies]);

  useEffect(() => {
    // Check if the current environment is a webview.
    if (isWebView) {
      // Extract authentication data from the 'X-WebView' cookie.
      const authData = getAuthDataFromWebview(cookies[cookieNames.WEBVIEW_COOKIE]);
      if (authData?.accessToken && authData?.userID) {
        // Create a user object with the userID from the authentication data.
        const user = { userID: authData.userID };

        // persist the access token in a local storage service
        storageService.set(storageService.ACCESS_TOKEN, authData.accessToken);

        // Dispatch an action to update the authentication state in the application's Redux store.
        // This includes setting the access token and user information.
        dispatch(updateAuthState({
          accessToken: authData.accessToken,
          user: user,
        }));
      } else {
        setIsWebviewError(true);
      }
    }
  }, [isWebView, cookies]);

  useEffect(() => {
    if (loginStatus === Status.fulfilled) {
      cleverTapAnl.event({
        event_name: 'login_number_submit',
        event: {
          page_name: 'login',
          login_mode: 'otp',
          number_length: 10,
          is_tc_installed: false,
        },
      } as EventLoginNumberSubmit);

      if (onLoginSuccess) onLoginSuccess(mobileNumber);
      else
        history.replace({
          pathname: '/verifyotp',
          search: location.search,
          state: mobileNumber,
        });
    }
  }, [loginStatus]);

  useEffect(() => {
    eventService.startActionDuration('login_success');
    return () => {
      dispatch(updateAuthState({ loginStatus: '' }));
    };
  }, []);

  function handleChange({ value }: any) {
    setMobileNumber(value.substring(0, 10));
    if (loginError)
      dispatch(updateAuthState({ loginError: '' }));
  }

  function handleSubmit() {
    if (isLogging) return;

    dispatch(login({ mobileNumber }));
  }

  function renderForm() {
    return (
      <Form className={formClassName}>
        <DoconInput
          autoFocus
          name='mobileNumber'
          type='tel'
          prefix='+91'
          label='Enter Registered Mobile Number'
          disabled={isLogging}
          value={mobileNumber}
          onEnter={handleSubmit}
          onChange={handleChange}
        />

        <Body2 className={'infoText'} color='currentColor' hidden={hideInfoText}>
          <span>By continuing you agree to our  </span>
          <a href={`${BASE_URL}/terms`}>Terms of Use</a> <span>and our  </span>
          <a href={`${BASE_URL}/privacy`}>Privacy Policy</a>
        </Body2>

        <PrimaryButton
          isBlock
          title='Continue'
          icon={isLogging ? 'loading' : ''}
          disabled={mobileNumber?.length !== 10}
          onClick={handleSubmit}
        />
        <ErrorText title={loginError} />
      </Form>
    );
  }

  if (onlyForm) return renderForm();

  if (isWebviewError) {
    history.replace('/error');
  }
  return (
    <Root>
      {
        isMobile ? <EmrSMSModal /> : null
      }

      <SliderPane />

      <Section>
        {renderForm()}
      </Section>

      <Footer hidden={!checkVisibility()}>
        <Body2 title={'Download the app on'} color='currentColor' />
        <a href={downloadApp({ hasReturn: true })}>
          <Image src={downloadImage()} alt='' />
        </a>
      </Footer>
    </Root>
  );
}

export default Login;
